import {Injectable} from '@angular/core';
import {ApiCall, LogItem, QueuedItemResponse} from '../models/queue';
import {ApiService} from './api.service';
import {LangService, LoaderService} from 'ngx-satoris';
import {addMilliseconds, format, isAfter, isBefore, subMinutes} from 'date-fns';
import {Store} from '../models/store';
import {StorageService} from './storage.service';
import {isJsonParsable} from '../utils/parseMetadata';
import {ConsumeMode, OperationType} from '../models/request';
import {environment} from 'src/environments/environment';

interface QueueState {
  id: string;
  activeCallSignatures: string[];
  data: ApiCall[];
}

@Injectable({
  providedIn: 'root'
})
export class QueueService {
  public queue: ApiCall[] = [];
  public onNavDeleteOfflineQueueId: string;

  private activeCallSignatures = new Set<string>();
  private isProcessing = false;
  private isExporting = false;
  private isStopping = false;
  private processDelay = 10000;
  private showConsoleLogs = false;

  constructor(private api: ApiService,
              private lang: LangService,
              private storage: StorageService,
              private loader: LoaderService) {
    this.api.signOutObservable.subscribe(() => this.stop());
  }

  start(): Promise<void> {
    if(this.isProcessing) return;
    this.isStopping = false;
    this.showConsoleLogs && console.log('🪱 Starting queue service...');
    return this.loadQueueState().then(() => this.processQueue());
  }

  stop(): Promise<void> {
    this.showConsoleLogs && console.log('🪱 Stopping queue service...');
    this.isStopping = true;
    this.queue = [];
    this.activeCallSignatures.clear();
    this.isProcessing = false;
    this.isExporting = false;
    return this.saveQueueState();
  }

  requestInQueue(id: string): boolean {
    return this.queue.some(q => q.requestId === id);
  }

  isRequestRecentInQueue(documentNumber: string, nationality: string, consumeMode: ConsumeMode, minutes: number): boolean {
    const matchingItems = this.queue.filter(queueItems => {
      // Parse params from the queue item (IN for serialized / OUT for the 9 index)
      const params = isJsonParsable((queueItems?.params?.[0]?.[0] as any)?.serialized) || isJsonParsable((queueItems?.params as any)?.[9]);
      if(!params?.PassportNumber || !params?.Nationality) return false;
      return params.PassportNumber === documentNumber && params.Nationality === nationality;
    });
    if(matchingItems.length === 0) return false;
    const timeLimit = subMinutes(new Date(), minutes);
    for(const items of matchingItems) {
      const timestamp = new Date(items.timestamp);
      const isRecent = isAfter(timestamp, timeLimit);
      if(!isRecent) continue;
      if(items.dependentCalls && items.dependentCalls.length) {
        for(const depCall of items.dependentCalls) {
          const depParams = (depCall.params as any)[6];
          if(!depParams?.Operation) continue;
          const operationType = depParams.Operation;
          if((consumeMode === ConsumeMode.ENTRY || consumeMode === ConsumeMode.TRANSIT) &&  (operationType === OperationType.IN || operationType === OperationType.INRETURNED)) {
            return true;
          }
          if(consumeMode === ConsumeMode.EXIT && operationType === OperationType.OUT) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private getCallSignature(methodName: ApiCall['methodName'], params: any[]): string {
    return `${methodName}-${JSON.stringify(params)}`;
  }

  private generateCallId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  private canProcess(call: ApiCall): boolean {
    if(!call.parentId) return true;
    const hasParentInQueue = this.queue.some(q => q.id === call.parentId);
    if(hasParentInQueue) {
      this.showConsoleLogs && console.log('🪱 Call cannot be processed yet, parent still in queue:', call.methodName);
    }
    return !hasParentInQueue;
  }

  private removeCall(call: ApiCall, removeDependants?: boolean): void {
    const callIndex = this.queue.findIndex(c => c.id === call.id);
    if(callIndex > -1) {
      this.showConsoleLogs && console.log('🪱 Removing call from queue:', call.methodName);
      this.queue.splice(callIndex, 1);
      if(removeDependants) {
        this.showConsoleLogs && console.log('🪱 Also removing dependent calls for:', call.methodName);
        this.queue.filter(q => q.parentId === call.id).forEach(depCall => this.removeCall(depCall, true));
      }
    }
  }

  addToQueue(methodName: ApiCall['methodName'],
    params: any[],
    customTimeout?: number,
    dependentCalls: Array<ApiCall> = [],
    requestId?: string,
    mustHaveDependant?: boolean): Promise<QueuedItemResponse | any> {
    this.showConsoleLogs && console.log('🪱 Adding new call to queue:', methodName, 'with dependent calls:', dependentCalls.length);
    const parentId = this.generateCallId();
    const executeApiCall = (method: ApiCall['methodName'],
      methodParams: any[],
      depCalls?: Array<ApiCall>,
      depParentId?: string,
      parentResult?: any,
      isInitialAttempt: boolean = false): Promise<QueuedItemResponse | any> => {
      this.api.directReturnError = true;
      if(customTimeout) this.api.customTimeout = customTimeout;

      if(parentResult) {
        this.showConsoleLogs && console.log('🪱 Processing parameters with parent result for:', method);
        methodParams = methodParams.map(param => {
          if(typeof param === 'string' && param.startsWith('{{') && param.endsWith('}}')) {
            const key = param.slice(2, -2);
            const resultConfig = depCalls?.[0]?.useResultFromPrevious?.[key];

            if(resultConfig) {
              if(typeof resultConfig === 'object') {
                this.showConsoleLogs && console.log('🪱 Applying time offset to parameter:', key);
                return addMilliseconds(new Date(), resultConfig.addMilliseconds);
              } else {
                const path = resultConfig.split('.') || [];
                let value = parentResult;
                for(const prop of path) {
                  value = value[prop];
                }
                this.showConsoleLogs && console.log('🪱 Retrieved value from parent result:', key, value);
                return value;
              }
            }
            return param;
          }
          return param;
        });
      }

      const sanitizedParams = this.sanitizeParams(methodParams);
      const fct: (...args: any) => any = this.api[method];
      return fct.bind(this.api)(...sanitizedParams).then((result: any) => {
        this.api.directReturnError = false;
        this.api.customTimeout = undefined;
        this.showConsoleLogs && console.log('🪱 API call succeeded:', method, ' ✅');
        return result === null ? true : result;
      }).catch((error: any) => {
        this.api.directReturnError = false;
        this.api.customTimeout = undefined;
        this.showConsoleLogs && console.log('🪱 API call failed:', method, error);

        this.storage.saveToStorage<LogItem>(Store.LOGS_STORE, {id: new Date().getTime(), timestamp: new Date(), message: {method, methodParams, depCalls, depParentId, parentResult, mustHaveDependant, requestId}}, 1000).catch(console.error);

        if(error.name === 'TimeoutError') {
          return this.handleTimeoutError(error,
            method,
            methodParams,
            depCalls,
            depParentId,
            parentResult,
            mustHaveDependant,
            requestId);
        }

        if(isInitialAttempt) {
          // First attempt should reject on non-timeout errors with a generic translated message (string)
          // const translated = this.lang.transform('global.error');
          // return Promise.reject(translated || 'Error');
          console.warn('🪱 Error at first sending of queue:', method, methodParams);
        }

        // For subsequent/dependent calls, add to queue and return a queue id
        const newId = this.generateCallId();
        this.showConsoleLogs && console.log('🪱 Adding failed call to queue for retry:', method);
        return this.addCallToQueue({
          id: newId,
          methodName: method,
          params: methodParams as any,
          parentId: depParentId,
          error: error.message,
          requestId,
          mustHaveDependant,
          dependentCalls: depCalls,
          timestamp: new Date()
        }).then(() => {
          if(!this.isProcessing) this.processQueue();
          return {queueId: newId};
        });
      });
    };

    return executeApiCall(methodName, params, dependentCalls, undefined, undefined, true).then(result => {
      if(result && typeof result === 'object' && 'queueId' in result) return result;

      if(dependentCalls?.length && result !== null) {
        this.showConsoleLogs && console.log('🪱 Processing dependent calls for:', methodName);
        return dependentCalls.reduce((promise, depCall) => promise.then(() => executeApiCall(depCall.methodName,
          depCall.params as any,
          [depCall],
          parentId,
          result,
          false)),
        Promise.resolve(null)).then(() => result);
      }
      return result;
    });
  }

  addDependantCallToExistingQueue(queueId: string, newDependentCall: ApiCall): Promise<any> {
    this.showConsoleLogs && console.log('🪱 Searching for parent call:', queueId, 'to add dependent call:', newDependentCall.methodName);

    const existingCall = this.queue.find((q) => q.id === queueId);

    if(!existingCall) {
      const error = `No existing call in the queue with queueId "${queueId}" to add dependant call to.`;
      console.error(error);
      throw new Error(error);
    }

    this.showConsoleLogs && console.log('🪱 Found parent call, adding dependent call');

    if(newDependentCall.dependentCalls?.length) {
      this.showConsoleLogs && console.log('🪱 Processing nested dependent calls');
      newDependentCall.dependentCalls = newDependentCall.dependentCalls.map(depCall => {
        if(depCall.useResultFromFirst) {
          this.showConsoleLogs && console.log('🪱 Found useResultFromFirst in nested call:', depCall.methodName);
        }
        return depCall;
      });
    }

    existingCall.dependentCalls = [...(existingCall.dependentCalls || []), newDependentCall];

    this.queue = this.queue.map((q) => q.id === existingCall.id ? existingCall : q);

    return this.saveQueueState().then((): void => {
      if(!this.isProcessing) this.processQueue();
    });
  }

  cleanOfflineOrphans(): Promise<number> {
    this.showConsoleLogs && console.log('🪱 Starting cleanup of orphaned mustHaveDependant items');

    const initialLength = this.queue.length;
    this.showConsoleLogs && console.log('🪱 Initial queue:', this.queue);
    const itemsToRemove: ApiCall[] = this.queue.filter(item => item.mustHaveDependant && (!item.dependentCalls || item.dependentCalls?.length === 0));

    if(itemsToRemove.length === 0) {
      this.showConsoleLogs && console.log('🪱 No orphaned items found');
      return Promise.resolve(0);
    }

    this.showConsoleLogs && console.log(`🪱 Found ${itemsToRemove.length} orphaned items to remove`);

    itemsToRemove.forEach(item => {
      this.showConsoleLogs && console.log('🪱 Removing orphaned item:', item);
      this.storage.saveToStorage<LogItem>(Store.LOGS_STORE, {id: new Date().getTime(), timestamp: new Date(), queueItem: item, message: 'Removing orphaned item'}, 1000).catch(console.error);

      this.removeCall(item);
    });

    return this.saveQueueState().then(() => {
      const removedCount = initialLength - this.queue.length;
      console.log(`Cleanup complete, removed ${removedCount} items`);
      return removedCount;
    });
  }

  warnOfflineOrphan(): Promise<boolean | void> {
    return this.loader.loading(true, {type: 'error', custom: {innerHtml: this.lang.transform('warn.payment.offlineApplication.quit'), closeBtnLabel: 'no', icon: 'trash-can'}, btnLabel: 'yes'});
  }

  private addCallToQueue(call: ApiCall): Promise<void> {
    this.showConsoleLogs && console.log('🪱 Adding call to queue:', call.methodName);
    this.queue.push(call);
    return this.saveQueueState();
  }

  private replaceParamsWithPreviousResults(params: any[],
    useResultFromPrevious: {[key: string]: string | {path: string; addMilliseconds: number}},
    previousResult: any): any[] {
    this.showConsoleLogs && console.log('🪱 Starting parameter replacement:', {
      originalParams: params,
      config: useResultFromPrevious,
      availableResults: previousResult
    });

    return params.map(param => {
      if(typeof param === 'string' && param.startsWith('{{') && param.endsWith('}}')) {
        const key = param.slice(2, -2);
        const pathConfig = useResultFromPrevious[key];

        this.showConsoleLogs && console.log('🪱 Processing replacement for key:', key, {
          pathConfig,
          paramType: typeof pathConfig
        });

        if(!pathConfig) {
          this.showConsoleLogs && console.log('🪱 No configuration found for key:', key);
          return param;
        }

        if(typeof pathConfig === 'object') {
          this.showConsoleLogs && console.log('🪱 Processing object config for key:', key, pathConfig);

          let baseValue: any;
          const arrayMatch = pathConfig.path.match(/^(\w+)\[(\d+)\]$/);

          if(arrayMatch) {
            const [_, arrayName, index] = arrayMatch;
            const array = previousResult[arrayName];
            baseValue = Array.isArray(array) ? array[parseInt(index)] : null;
            this.showConsoleLogs && console.log('🪱 Retrieved array value:', baseValue);
          } else {
            const path = pathConfig.path.split('.');
            baseValue = previousResult;
            for(const prop of path) {
              baseValue = baseValue?.[prop];
            }
            this.showConsoleLogs && console.log('🪱 Retrieved path value:', baseValue);
          }

          if(pathConfig.addMilliseconds) {
            const finalDate = addMilliseconds(new Date(), pathConfig.addMilliseconds);
            this.showConsoleLogs && console.log('🪱 Applied time offset, final value:', finalDate);
            return finalDate;
          }

          return baseValue;
        }

        const path = pathConfig.split('.');
        let value = previousResult;

        const arrayMatch = pathConfig.match(/^(\w+)\[(\d+)\]$/);
        if(arrayMatch) {
          const [_, arrayName, index] = arrayMatch;
          const array = previousResult[arrayName];
          value = Array.isArray(array) ? array[parseInt(index)] : null;
        } else {
          for(const prop of path) {
            value = value?.[prop];
          }
        }

        this.showConsoleLogs && console.log('🪱 Final value for key:', key, value);
        return value;
      }
      return param;
    });
  }

  private handleTimeoutError(error: any,
    currentMethodName: ApiCall['methodName'],
    currentParams: any[],
    depCalls?: Array<ApiCall>,
    depParentId?: string,
    parentResult?: any,
    mustHaveDependant?: boolean,
    requestId?: string): Promise<QueuedItemResponse> {
    this.showConsoleLogs && console.log('🪱 Handling timeout error for:', currentMethodName);

    const queueId = this.generateCallId();

    if(parentResult) {
      this.showConsoleLogs && console.log('🪱 Handling timeout for dependent call:', currentMethodName);
      const newCall: ApiCall = {
        id: queueId,
        methodName: currentMethodName,
        params: currentParams as any,
        error: error.message,
        requestId: requestId,
        mustHaveDependant: false,
        dependentCalls: [],
        timestamp: new Date()
      };

      this.storage.saveToStorage<LogItem>(Store.LOGS_STORE, {id: new Date().getTime(), timestamp: new Date(), queueItem: newCall, message: 'Handling timeout for dependent call'}, 1000).catch(console.error);
      return this.addCallToQueue(newCall).then(() => ({queueId}));
    }

    const queueEntry: ApiCall = {
      id: queueId,
      methodName: currentMethodName,
      params: currentParams as any,
      parentId: depParentId,
      error: error.message,
      requestId: requestId,
      mustHaveDependant: mustHaveDependant ?? false,
      dependentCalls: depCalls || [],
      timestamp: new Date()
    };

    this.storage.saveToStorage<LogItem>(Store.LOGS_STORE, {id: new Date().getTime(), timestamp: new Date(), queueItem: queueEntry}, 1000).catch(console.error);

    return this.addCallToQueue(queueEntry).then(() => {
      if(depCalls?.length) {
        this.showConsoleLogs && console.log('🪱 Adding dependent calls after parent timeout');
        return depCalls.reduce((promise, call) => promise.then(() => {
          const depCall: ApiCall = {
            id: this.generateCallId(),
            methodName: call.methodName,
            params: call.params as any,
            parentId: queueEntry.id,
            requestId: call.requestId,
            useResultFromPrevious: call.useResultFromPrevious,
            useResultFromFirst: call.useResultFromFirst,
            processNotBefore: call.processNotBefore,
            processMillisAfterParent: call.processMillisAfterParent,
            timestamp: new Date()
          };

          this.storage.saveToStorage<LogItem>(Store.LOGS_STORE, {id: new Date().getTime(), timestamp: new Date(), queueItem: queueEntry, message: 'Adding dependent calls after parent timeout'}, 1000).catch(console.error);
          return this.addCallToQueue(depCall);
        }), Promise.resolve());
      }
      return Promise.resolve();
    }).then(() => {
      if(!this.isProcessing) this.processQueue();
      return {queueId};
    });
  }

  private processQueue(): Promise<void> {
    if(this.isStopping || this.isProcessing || this.queue.length === 0 || this.isExporting) return Promise.resolve();

    this.isProcessing = true;
    this.showConsoleLogs && console.log('🪱 Starting queue processing, length:', this.queue.length);

    const processNext = (): Promise<void> => {
      if(this.queue.length === 0 || this.isStopping || this.isExporting) {
        this.showConsoleLogs && console.log('🪱 Queue processing finished or stopped');
        this.isProcessing = false;
        return Promise.resolve();
      }

      const currentCall: ApiCall = this.queue[0];
      const signature = this.getCallSignature(currentCall.methodName, currentCall.params as any);

      currentCall.isProcessing = false;

      if((currentCall.processNotBefore && isBefore(new Date(), currentCall.processNotBefore)) ||
        (currentCall.mustHaveDependant && (!currentCall.dependentCalls || currentCall.dependentCalls.length === 0))) {
        this.showConsoleLogs && console.log('🪱 Call cannot be processed now:', currentCall.methodName, {
          hasTimeConstraint: !!currentCall.processNotBefore,
          needsDependents: currentCall.mustHaveDependant
        });
        this.queue.push(this.queue.shift()!);
        return this.saveQueueState().then(() => new Promise<void>(resolve => setTimeout(() => {
          resolve(processNext());
        }, this.processDelay)));
      }

      if(this.canProcess(currentCall)) {
        this.showConsoleLogs && console.log('🪱 Processing call:', currentCall.methodName);
        this.activeCallSignatures.add(signature);
        currentCall.isProcessing = true;

        return this.saveQueueState().then(() => {
          const method = (this.api[currentCall.methodName] as Function).bind(this.api);

          const sanitizedParams = this.sanitizeParams(currentCall.params as any);
          return method(...sanitizedParams);
        }).then(result => {
          this.showConsoleLogs && console.log('🪱 Call succeeded:', currentCall.methodName, ' ✅');
          this.activeCallSignatures.delete(signature);
          currentCall.isProcessing = false;

          const resultWithDate = {...result, completionDate: new Date()};

          if(currentCall.dependentCalls?.length) {
            this.showConsoleLogs && console.log('🪱 Processing dependent calls, adding completion date to result');
            return currentCall.dependentCalls.reduce((promise, depCall) => promise.then(() => {
              let depParams = depCall.params;

              if(depCall.useResultFromFirst) {
                this.showConsoleLogs && console.log('🪱 Applying useResultFromFirst transformations for:', depCall.methodName);
                depParams = this.replaceParamsWithPreviousResults(depParams as any, depCall.useResultFromFirst, resultWithDate) as any;
              }

              if(depCall.useResultFromPrevious) {
                this.showConsoleLogs && console.log('🪱 Applying useResultFromPrevious transformations for:', depCall.methodName);
                depParams = this.replaceParamsWithPreviousResults(depParams as any, depCall.useResultFromPrevious, resultWithDate) as any;
              }

              if(depCall.dependentCalls?.length) {
                this.showConsoleLogs && console.log('🪱 Processing nested dependent calls');
                depCall.dependentCalls = depCall.dependentCalls.map(subDepCall => {
                  const newSubDepCall = {...subDepCall};

                  if(subDepCall.useResultFromFirst) {
                    this.showConsoleLogs && console.log('🪱 Applying useResultFromFirst to nested call:', subDepCall.methodName);
                    newSubDepCall.params = this.replaceParamsWithPreviousResults(subDepCall.params as any,
                      subDepCall.useResultFromFirst,
                      resultWithDate) as any;
                  }

                  return newSubDepCall;
                });
              }

              const newIndependentCall: ApiCall = {
                id: this.generateCallId(),
                methodName: depCall.methodName,
                params: depParams as any,
                requestId: depCall.requestId,
                mustHaveDependant: false,
                dependentCalls: depCall.dependentCalls,
                isProcessing: false,
                processNotBefore: depCall.processNotBefore || (depCall.processMillisAfterParent ? addMilliseconds(new Date(), depCall.processMillisAfterParent) : undefined)
              };

              this.showConsoleLogs && console.log('🪱 Adding processed dependent call to queue:', {
                methodName: newIndependentCall.methodName,
                params: newIndependentCall.params
              });

              return this.addCallToQueue(newIndependentCall);
            }), Promise.resolve()).then(() => {
              currentCall.isProcessing = false;
              this.removeCall(currentCall);
              return this.saveQueueState();
            });
          }

          currentCall.isProcessing = false;
          this.removeCall(currentCall);
          return this.saveQueueState();
        }).then(() => new Promise<void>(resolve => setTimeout(() => {
          resolve(processNext());
        }, this.processDelay))).catch((error: any) => {
          const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
          this.showConsoleLogs && console.log('🪱 Call error:', currentCall.methodName, errorMessage);

          this.activeCallSignatures.delete(signature);
          currentCall.isProcessing = false;
          currentCall.error = errorMessage;

          if(errorMessage.includes('client.extended.uuidUsed') || errorMessage.includes('client.extended.maxUsageCountReached') || errorMessage.includes('client.extended.duplicateConsumption')) {
            this.storage.saveToStorage<LogItem>(Store.LOGS_STORE, {id: new Date().getTime(), timestamp: new Date(), queueItem: currentCall, message: errorMessage}, 1000).catch(console.error);
            this.removeCall(currentCall, true);
          } else {
            this.queue.push(this.queue.shift()!);
          }

          return this.saveQueueState().then(() => new Promise<void>(resolve => setTimeout(() => {
            resolve(processNext());
          }, this.processDelay)));
        });
      }

      currentCall.isProcessing = false;
      this.queue.push(this.queue.shift()!);
      return this.saveQueueState().then(() => new Promise<void>(resolve => setTimeout(() => {
        resolve(processNext());
      }, this.processDelay)));
    };

    return processNext().catch(err => {
      console.error('Queue processing error:', err);
      this.isProcessing = false;
      this.queue.forEach(call => call.isProcessing = false);
      return Promise.resolve();
    });
  }

  private saveQueueState(): Promise<void> {
    if(!this.api.userInfo) {
      this.showConsoleLogs && console.log('🪱 Cannot save queue state: no user info');
      return Promise.reject('No user info');
    }

    const state: QueueState = {
      id: this.api.userInfo.id + '',
      activeCallSignatures: Array.from(this.activeCallSignatures),
      data: this.queue
    };

    this.showConsoleLogs && console.log('🪱 Saving queue state, queue length:', this.queue.length);

    return new Promise<void>((resolve, reject) => {
      const trans = this.storage.idbstorage.transaction(Store.QUEUE_STORE, 'readwrite');
      const objStore = trans?.objectStore?.(Store.QUEUE_STORE);
      const request = objStore.put(state);

      request.onsuccess = () => {
        this.showConsoleLogs && console.log('🪱 Queue state saved successfully');
        resolve();
      };

      request.onerror = () => {
        console.error('🪱 Failed to save queue state:', request.error);
        reject(request.error);
      };
    });
  }

  private loadQueueState(): Promise<void> {
    this.showConsoleLogs && console.log('🪱 Loading queue state');
    return this.storage.readStorage<QueueState>(Store.QUEUE_STORE).then(state => {
      if(state) {
        this.showConsoleLogs && console.log('🪱 Queue state loaded, calls:', state.data?.length || 0);
        this.queue = state.data || [];
        if(environment.autoDiscardOldQueue) {
          this.queue = this.queue.filter(call => (call.params as any)[call.params.length - 1] === environment.appVersion.join('.'));
        }
        this.showConsoleLogs && console.log('🪱 Queue cleared from version, remaining:', this.queue.length);
        this.activeCallSignatures = new Set();
        this.showConsoleLogs && console.log('🪱 Active call signatures cleared after load');
        return this.saveQueueState();
      }
    });
  }

  openImportDialog(): void {
    this.showConsoleLogs && console.log('🪱 Opening import dialog');
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e: any) => {
      if(e.target.files.length > 0) {
        this.importQueueFromJSON(e.target.files[0]);
      }
    };
    input.click();
  }

  openExportDialog(event: any): void {
    this.showConsoleLogs && console.log('🪱 Opening export dialog');
    event.stopPropagation();
    this.loader.loading(true, {
      type: 'info',
      btnLabel: 'queue.export',
      custom: {
        innerHtml: this.lang.transform('queue.export.desc'),
        icon: 'cloud-arrow-down'
      }
    }).then((done: boolean) => {
      if(done) this.exportQueueToJSON();
    });
  }

  exportQueueToJSON(reload = true): void {
    if(!this.queue.length) return;
    this.showConsoleLogs && console.log('🪱 Starting queue export');
    this.isExporting = true;
    const jsonContent = JSON.stringify(this.queue, null, 2);
    const blob = new Blob([jsonContent], {type: 'application/json;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `BMS_userID_${this.api.userInfo.id}_placeID_${this.api.userPlaceId}_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.json`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    this.showConsoleLogs && console.log('🪱 Queue exported, clearing current queue');
    this.queue = [];
    this.saveQueueState().then(() => {
      this.isExporting = false;
      if(reload) {
        window.location.reload();
      }
    });
  }

  private importQueueFromJSON(file: File): void {
    this.showConsoleLogs && console.log('🪱 Starting queue import from file');
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const jsonContent = e.target.result;
      try {
        const importedQueue: ApiCall[] = JSON.parse(jsonContent);
        this.showConsoleLogs && console.log('🪱 Parsed imported queue, calls:', importedQueue.length);

        importedQueue.forEach(call => {
          const newCall: ApiCall = {
            id: this.generateCallId(),
            methodName: call.methodName,
            params: call.params as any,
            parentId: call.parentId,
            requestId: call.requestId,
            mustHaveDependant: call.mustHaveDependant,
            dependentCalls: call.dependentCalls,
            useResultFromPrevious: call.useResultFromPrevious,
            useResultFromFirst: call.useResultFromFirst,
            processNotBefore: call.processNotBefore,
            processMillisAfterParent: call.processMillisAfterParent
          };

          this.showConsoleLogs && console.log('🪱 Adding imported call to queue:', newCall.methodName);
          this.queue.push(newCall);
        });

        this.saveQueueState().then(() => {
          if(!this.isProcessing) {
            this.showConsoleLogs && console.log('🪱 Starting queue processing after import');
            this.processQueue();
          }
        });
      } catch (error) {
        console.error('Failed to parse imported JSON:', error);
      }
    };
    reader.readAsText(file);
  }

  updateCallMustHaveDependant(methodName: ApiCall['methodName'],
    params: any[],
    mustHaveDependant: boolean): Promise<void> {
    this.showConsoleLogs && console.log('🪱 Updating mustHaveDependant for call:', methodName);
    const existingCall = this.queue.find((q) => q.methodName === methodName && JSON.stringify(q.params) === JSON.stringify(params));

    if(!existingCall) {
      this.showConsoleLogs && console.log('🪱 Call not found in queue:', methodName);
      return Promise.resolve();
    }

    existingCall.mustHaveDependant = mustHaveDependant;
    this.showConsoleLogs && console.log('🪱 Updated mustHaveDependant for call:', methodName, 'to:', mustHaveDependant);

    this.queue = this.queue.map((q) => q.id === existingCall.id ? existingCall : q);
    return this.saveQueueState();
  }

  removeFromQueue(criteria: {id?: string | undefined, methodName?: ApiCall['methodName'], params?: any[]}, removeDependants: boolean = true): Promise<boolean> {
    this.showConsoleLogs && console.log('🪱 Attempting to remove item from queue with criteria:', {...criteria, params: criteria.params ? 'provided' : 'not provided'});

    let itemToRemove: ApiCall | undefined;

    if(criteria.id && criteria.id.length > 0) {
      itemToRemove = this.queue.find(item => item.id === criteria.id);
      this.showConsoleLogs && console.log('🪱 Searching by id:', criteria.id, 'found:', !!itemToRemove);
    }

    if(!itemToRemove && criteria.methodName && criteria.params) {
      const signature = this.getCallSignature(criteria.methodName, criteria.params);
      itemToRemove = this.queue.find(item => this.getCallSignature(item.methodName, item.params as any) === signature);
      this.showConsoleLogs && console.log('🪱 Searching by signature:', signature, 'found:', !!itemToRemove);
    }

    if(!itemToRemove) {
      this.showConsoleLogs && console.log('🪱 No matching item found in queue');
      return Promise.resolve(false);
    }

    this.showConsoleLogs && console.log('🪱 Found item to remove:', {methodName: itemToRemove.methodName, id: itemToRemove.id || 'no id', hasDependents: itemToRemove.dependentCalls?.length > 0});

    this.removeCall(itemToRemove, removeDependants);

    const signature = this.getCallSignature(itemToRemove.methodName, itemToRemove.params as any);
    this.activeCallSignatures.delete(signature);

    return this.saveQueueState().then(() => {
      this.showConsoleLogs && console.log('🪱 Queue state saved after removal');
      return true;
    });
  }

  private sanitizeParams(params: any[]): any[] {
    this.showConsoleLogs && console.log('🪱 Sanitizing params:', params);
    const transformValue = (value: any): any => {
      if(value === null || value === 'null' || value === 'undefined') {
        return undefined;
      }
      return value;
    };

    this.showConsoleLogs && console.log('🪱 Sanitized params:', params.map(param => transformValue(param)));
    return params.map(param => transformValue(param));
  }
}
